import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { ConsentValidationResult } from "@/types/consent";

export const ProtectedRoute = () => {
  const { isAuthenticated, user, isLoading } = useAuthStore();
  const location = useLocation();
  const [consentValidation, setConsentValidation] = useState<ConsentValidationResult | null>(null);
  const [isCheckingConsents, setIsCheckingConsents] = useState(false);
  const [isCollaborator, setIsCollaborator] = useState<boolean | null>(null);

  // All hooks must be called before any early returns
  // Check consent validation for authenticated users
  useEffect(() => {
    const checkConsents = async () => {
      if (!isAuthenticated || !user || isLoading) return;

      // Skip consent check for certain pages
      const isConsentPage = location.pathname === "/consent";
      const isOnboardingPage = location.pathname.startsWith("/onboarding");
      const isCallbackPage = location.pathname.startsWith("/integrations/youtube/callback");
      const isProfilePage = location.pathname === "/profile";
      const isAdminPage = location.pathname.startsWith("/admin");

      if (isConsentPage || isCallbackPage || isAdminPage) {
        return;
      }

      // Optimization: If we already have a valid validation and the path didn't change to a sensitive area, don't re-validate
      // For now, we only check basic_access globally.
      if (consentValidation?.is_valid) {
        return;
      }

      try {
        // Debounce or avoid rapid checks
        setIsCheckingConsents(true);
        const validation = await apiClient.validateConsents("basic_access") as ConsentValidationResult;
        setConsentValidation(validation);
      } catch (error) {
        console.error("Error checking consents:", error);
        // Don't block access if consent check fails
        setConsentValidation({ is_valid: true, missing_consents: [], expired_consents: [], required_actions: [] });
      } finally {
        setIsCheckingConsents(false);
      }
    };

    checkConsents();
  }, [isAuthenticated, user, isLoading, location.pathname]);

  // Check if user is a collaborator on any project
  useEffect(() => {
    const checkCollaboratorStatus = async () => {
      if (!isAuthenticated || !user || isLoading) {
        setIsCollaborator(null);
        return;
      }

      try {
        // Check if user has any collaborations
        const response = await apiClient.getProjects();
        const projectsArray = response.projects || [];
        
        // Check if user is a collaborator (not owner) on any project
        const hasCollaborations = projectsArray.some((project: any) =>
          project.owner_id !== user._id &&
          project.collaborators?.some((collab: any) => collab.user_id === user._id)
        );
        
        setIsCollaborator(hasCollaborations);
        
        if (hasCollaborations) {
          console.log("🐛 [DEBUG] User is a collaborator on projects:", {
            userId: user._id,
            email: user.email,
            collaboratorProjects: projectsArray.filter((p: any) =>
              p.owner_id !== user._id &&
              p.collaborators?.some((c: any) => c.user_id === user._id)
            ).map((p: any) => ({ id: p._id, title: p.title }))
          });
        }
      } catch (error) {
        console.error("Error checking collaborator status:", error);
        setIsCollaborator(false);
      }
    };

    checkCollaboratorStatus();
  }, [isAuthenticated, user, isLoading]);

  // Early returns for authentication checks
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If we're authenticated but still loading the user or user data is missing
  // (which happens during the initial checkAuth call), show a loading state
  // to prevent premature redirection
  if (isLoading || (isAuthenticated && !user)) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading account...</p>
        </div>
      </div>
    );
  }

  // Show loading while checking consents
  if (isCheckingConsents) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is admin, redirect to admin dashboard
  if (user?.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }

  // Check consent validation - redirect to consent page if needed
  const isConsentPage = location.pathname === "/consent";
  if (consentValidation && !consentValidation.is_valid && !isConsentPage) {
    const currentPath = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/consent?redirect=${currentPath}`} replace />;
  }

  // Check if user has completed onboarding
  // We check specifically for the flag, but fallback to name check for legacy users if needed
  // ideally we should migrate all users to have the flag
  const hasCompletedOnboarding = user?.onboarding_completed;
  
  // Allow initial assessment page as part of the onboarding flow
  const isOnboardingPage = location.pathname.startsWith("/onboarding");
  const isBrandOnboardingPage = location.pathname.startsWith("/brand-onboarding");
  const isInitialAssessmentPage = location.pathname === "/initial-assessment";
  const isCallbackPage = location.pathname.startsWith("/integrations/youtube/callback");
  const isProfilePage = location.pathname === "/profile";
  // The end of signup in inventory mode, and therefore part of the onboarding flow exactly as
  // /initial-assessment is at the end of the scoring flow. Without this exemption the guard below
  // bounces a creator who has just finished — file in S3, project created, placement run complete —
  // back to the first page of the wizard, because the redirect can land before
  // onboarding_completed has been written.
  const isUploadThanksPage = location.pathname === "/upload-thanks";

  const isAnyOnboardingPage = isOnboardingPage || isBrandOnboardingPage;

  // If user is on brand onboarding page and is a brand user, allow access
  if (isBrandOnboardingPage && user?.role === 'brand_user') {
    return <Outlet />;
  }

  // If user has completed onboarding and tries to access onboarding pages (but NOT initial assessment), redirect to dashboard
  // Initial assessment page should be accessible even after onboarding completion
  if (hasCompletedOnboarding && isAnyOnboardingPage && !isInitialAssessmentPage) {
    // Redirect to appropriate dashboard based on user role
    if (user?.role === 'brand_user') {
      return <Navigate to="/brand-dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  // Only redirect to onboarding if user hasn't completed it yet AND they're not a collaborator
  if (!hasCompletedOnboarding && !isOnboardingPage && !isInitialAssessmentPage && !isCallbackPage && !isProfilePage && !isConsentPage && !isUploadThanksPage) {
    // Check if user is coming from collaboration invite or is already a collaborator
    const isFromCollaborationInvite = location.pathname.includes('/invite/') ||
                                     location.search.includes('redirectTo') && location.search.includes('invite');
    
    // Check if user has recently completed collaborator onboarding (within last 5 minutes)
    const recentCollaboratorOnboarding = localStorage.getItem('collaborator_onboarding_completed');
    const isRecentCollaborator = recentCollaboratorOnboarding &&
      (Date.now() - parseInt(recentCollaboratorOnboarding)) < 5 * 60 * 1000; // 5 minutes
    
    // Add diagnostic logging for collaboration routing issue
    console.log("🐛 [DEBUG] Onboarding routing decision:", {
      userId: user?._id,
      email: user?.email,
      hasCompletedOnboarding,
      isCollaborator,
      isFromCollaborationInvite,
      isRecentCollaborator,
      currentPath: location.pathname,
      searchParams: location.search,
      shouldSkipOnboarding: isCollaborator === true || isFromCollaborationInvite || isRecentCollaborator
    });
    
    // Skip onboarding for collaborators or users coming from collaboration invites
    if (isCollaborator === true || isFromCollaborationInvite || isRecentCollaborator) {
      console.log("🐛 [DEBUG] Skipping onboarding for collaborator - redirecting to dashboard");
      return <Navigate to="/dashboard" replace />;
    }
    
    // Still checking collaborator status, show loading
    if (isCollaborator === null) {
      return (
        <div className="flex h-screen w-full items-center justify-center bg-gray-50">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Checking access...</p>
          </div>
        </div>
      );
    }
    
    // Check if user is a brand user and redirect to brand onboarding
    if (user?.role === 'brand_user') {
      return <Navigate to="/brand-onboarding/company-info" replace />;
    }
    
    // Regular user - proceed to creator onboarding
    return <Navigate to="/onboarding-v2" replace />;
  }

  return <Outlet />;
};